// Leyenda del modo Sandbox: arriba de TODA la consola mientras está encendido,
// con el botón para apagarlo ahí mismo.
//
// Vive junto a las rutas (como BillingGate) para cubrir todas las pantallas sin
// tocar cada layout. Es una pastilla flotante centrada y no una franja de lado
// a lado: los layouts miden la pantalla completa y una franja les comería
// altura o taparía su cabecera.
//
// Dice cuántas unidades de ejemplo se están viendo y cuántos equipos reales tiene
// la cuenta, que aquí no aparecen. Sin ningún equipo real, invita a vincular uno.
//
// También muestra el aviso de solo lectura: el interceptor de la API detiene las
// escrituras de negocio y aquí se ofrece salir a la cuenta real.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { LINK_DEVICE_PATH } from "../../pages/devices/link/linkDevice";
import { sampleDomains, sampleUnits, toggleSandbox, useSandboxStore } from "./sandboxStore";
import { SANDBOX_READ_ONLY_EVENT } from "./sandboxView";
import { canResumeOnboarding, RESUME_WELCOME_PATH, WELCOME_PATH } from "../../pages/onboarding/onboardingLogic";

// «Termina de configurar tu cuenta» (CC-CONSOLE#1550): quien entró al mapa con
// «Ver mi mapa con datos de ejemplo» dejó la bienvenida pendiente. Se pregunta
// UNA vez por sesión (token) y sólo con el Sandbox encendido.
let resumeCache: { token: string; promise: Promise<boolean> } | null = null;
function loadCanResume(): Promise<boolean> {
  const token = localStorage.getItem("token") || "";
  if (!token) return Promise.resolve(false);
  if (resumeCache?.token === token) return resumeCache.promise;
  const promise = import("../../pages/onboarding/onboardingService")
    .then(({ fetchOnboardingStatus }) => fetchOnboardingStatus())
    .then((status) => canResumeOnboarding(status))
    .catch(() => false);
  resumeCache = { token, promise };
  return promise;
}

/** Sólo pruebas. */
export function _resetResumeCache(): void {
  resumeCache = null;
}

export default function SandboxBanner() {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const enabled = useSandboxStore((s) => s.enabled);
  const canToggle = useSandboxStore((s) => s.canToggle);
  const canLink = useSandboxStore((s) => s.canLink);
  const busy = useSandboxStore((s) => s.busy);
  const error = useSandboxStore((s) => s.error);
  const counts = useSandboxStore((s) => s.counts);
  const domains = useSandboxStore((s) => s.domains);
  const total = useSandboxStore((s) => s.total);
  const realCounts = useSandboxStore((s) => s.realCounts);
  const load = useSandboxStore((s) => s.load);
  const [canResume, setCanResume] = useState(false);
  const [readOnlyNotice, setReadOnlyNotice] = useState(false);

  // En cada navegación: barato (no vuelve a pedir si la sesión es la misma) y
  // cubre el caso de entrar o salir de la sesión sin recargar.
  useEffect(() => {
    void load();
  }, [pathname, load]);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    void loadCanResume().then((value) => {
      if (active) setCanResume(value);
    });
    return () => {
      active = false;
    };
  }, [enabled]);

  useEffect(() => {
    const show = () => setReadOnlyNotice(true);
    window.addEventListener(SANDBOX_READ_ONLY_EVENT, show);
    return () => window.removeEventListener(SANDBOX_READ_ONLY_EVENT, show);
  }, []);

  if (!enabled) return null;

  const sample = sampleUnits(counts);
  // Sin `realCounts` (API anterior) no se afirma nada sobre lo real.
  const real = realCounts ? realCounts.devices : null;
  const onLinkPage = pathname.startsWith(LINK_DEVICE_PATH);

  return (
    <div
      role="status"
      data-testid="sandbox-banner"
      className="pointer-events-none fixed inset-x-0 top-1 z-[9000] flex justify-center px-2"
    >
      <div className="pointer-events-auto flex max-w-full items-center gap-3 rounded-full border border-warning/50 bg-warning px-4 py-1.5 text-xs font-medium text-black shadow-lg">
        <i className="ri-flask-line text-sm" aria-hidden="true" />
        <span className="truncate">
          <strong className="font-semibold">{t("sandbox.bannerTitle", "Modo Sandbox")}</strong>
          <span className="hidden sm:inline">
            {" · "}
            {real === null ? (
              t("sandbox.bannerText", "Estás viendo una empresa de ejemplo, de solo lectura.")
            ) : (
              <span data-testid="sandbox-counts">
                {t("sandbox.sampleUnits", "{{count}} unidades de ejemplo", { count: sample })}
                {/* CC-CONSOLE#1576: el conjunto cubre todas las funcionalidades; un resumen corto. */}
                {total > 0 && (
                  <span className="hidden lg:inline" data-testid="sandbox-summary">
                    {" · "}
                    {t("sandbox.sampleRecords", "{{count}} registros en {{modules}} módulos", { count: total, modules: sampleDomains(domains) })}
                  </span>
                )}
                {" · "}
                {t("sandbox.realDevices", "{{count}} equipos reales en tu cuenta", { count: real })}
              </span>
            )}
          </span>
        </span>
        {canLink && real === 0 && !onLinkPage && (
          <Link
            to={LINK_DEVICE_PATH}
            data-testid="sandbox-link-device"
            className="hidden shrink-0 rounded-full border border-black/30 px-3 py-0.5 text-[11px] font-semibold text-black transition-colors hover:bg-black/10 sm:inline-block"
          >
            {t("sandbox.linkDevice", "Vincular dispositivo")}
          </Link>
        )}
        {canResume && !pathname.startsWith(WELCOME_PATH) && (
          <Link
            to={RESUME_WELCOME_PATH}
            data-testid="sandbox-resume-onboarding"
            onClick={() => {
              resumeCache = null;
            }}
            className="shrink-0 rounded-full border border-black/30 px-3 py-0.5 text-[11px] font-semibold text-black transition-colors hover:bg-black/10"
          >
            {t("sandbox.resumeOnboarding", "Termina de configurar tu cuenta")}
          </Link>
        )}
        {readOnlyNotice && (
          <span role="alert" data-testid="sandbox-read-only" className="shrink-0 font-semibold">
            {t("sandbox.readOnly", "Para guardar, sal a tu cuenta.")}
          </span>
        )}
        {error && <span className="hidden sm:inline">{t("sandbox.error", "No se pudo cambiar. Intenta de nuevo.")}</span>}
        {canToggle && (
          <button
            type="button"
            onClick={() => void toggleSandbox(false)}
            disabled={busy}
            className="shrink-0 rounded-full bg-black/85 px-3 py-0.5 text-[11px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-60"
          >
            {busy ? t("sandbox.turningOff", "Saliendo…") : t("sandbox.turnOff", "Salir a mi cuenta")}
          </button>
        )}
      </div>
    </div>
  );
}
